import { useEffect } from 'react'
import { ClipboardCheck } from 'lucide-react'
import type { CategoryState, ExtractedComplianceItem } from '@/types/extraction'
import { OverviewStatTile } from '@/components/overview/OverviewStatTile'
import { useCoverageStore } from '@/stores/coverage-store'
import { isRanAndEmpty } from '@/stores/extraction-store'
import { countMandatoryRisk, complianceMatrixLink } from '@/lib/compliance-position'

/**
 * Compliance tile — coverage-first (Specs/compliance-checklist-spec.md §4.3).
 * Counts run against the narrative denominator ("4 / 16 addressed", not
 * "/53"), with the other classes visibly accounted for in the subline.
 * Falls back to obligation-status counts until coverage loads.
 */
export function ComplianceSnapshot({
  bidId,
  items,
  categoryState,
  loading,
}: {
  bidId: string
  items: ExtractedComplianceItem[]
  categoryState: CategoryState
  loading?: boolean
}) {
  const loadCoverage = useCoverageStore((s) => s.load)
  const coverage = useCoverageStore((s) => s.coverage)
  const coverageBidId = useCoverageStore((s) => s.bidId)

  useEffect(() => {
    void loadCoverage(bidId)
  }, [bidId, loadCoverage])

  const total = items.length
  const done = items.filter((item) => item.status === 'completed').length
  const summary = coverageBidId === bidId ? coverage?.complianceSummary : undefined
  const tracked = summary ? total - summary.total : 0
  const { atRisk } = countMandatoryRisk(items)
  // Use the shared selector rather than re-deriving "ran and found nothing"
  // inline, so this copy can never disagree with the tab-collapse decision in
  // AnalysisSection or the empty state in ExtractionTable.
  const ranAndEmpty = isRanAndEmpty(categoryState)

  return (
    <OverviewStatTile
      icon={ClipboardCheck}
      label="Compliance"
      value={
        summary && summary.total > 0 ? (
          <span>
            {summary.addressed}
            <span className="text-base font-normal text-muted-foreground"> / {summary.total} addressed</span>
          </span>
        ) : (
          total
        )
      }
      // `loading` only gates the tile's value, so the sub line has to be held
      // back here too — mid-run the counts are still zero and the tile would
      // otherwise claim a verdict while the analysis is still finding out.
      sub={
        loading ? undefined : atRisk > 0 ? (
          // Deliberately replaces the coverage subline: the bar cell has one
          // line, and a pass/fail requirement we can't meet outranks an
          // unmapped one. The tile's link follows the warning (below).
          <span className="shrink-0 font-medium text-[hsl(var(--destructive))]">
            {atRisk} mandatory at risk
          </span>
        ) : summary && summary.total > 0 ? (
          <span className="flex min-w-0 items-center gap-1.5">
            {summary.unmapped > 0 && (
              <span className="shrink-0 font-medium text-[hsl(var(--status-warning))]">
                {summary.unmapped} unmapped
              </span>
            )}
            {tracked > 0 && (
              <span className="min-w-0 truncate">+{tracked} attachments, forms &amp; post-award tracked</span>
            )}
            {summary.unmapped === 0 && tracked === 0 && <span className="shrink-0">{done} of {total} done</span>}
          </span>
        ) : total > 0 ? (
          `${done} of ${total} done`
        ) : ranAndEmpty ? (
          'Nothing found'
        ) : (
          'Not analysed yet'
        )
      }
      progress={summary && summary.total > 0 ? (summary.addressed / summary.total) * 100 : undefined}
      to={
        atRisk > 0
          ? complianceMatrixLink(bidId, { position: 'at_risk', criticality: 'mandatory' })
          : complianceMatrixLink(bidId)
      }
      loading={loading}
    />
  )
}
